import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useClientTranslation } from '@/lib/core/i18n';
import { randomUuid } from '@/lib/core/utils';
import { Toast } from '@/components/ui/toast';
import {
  EatAndDrinkBookFlowConfirmationElementContentModelContentType,
  EatAndDrinkBookFlowConfirmationPropertiesModel,
  EatAndDrinkBookFlowMoreDetailsElementContentModelContentType,
  EatAndDrinkBookFlowMoreDetailsPropertiesModel,
  EatAndDrinkBookFlowWhereHowManyWhenElementContentModelContentType,
  EatAndDrinkBookFlowWhereHowManyWhenPropertiesModel,
  SeatingReservationDto,
} from '@/services/platform/api/model';
import type { BookingState, BookFlowEatAndDrinkProps } from './types';
import { EatAndDrinkBook } from './book';
import { EatAndDrinkDetails } from './details';
import { ConfirmationBook } from './confirmation/book';
import { ConfirmationRequest } from './confirmation/request';

export type { BookingState, BookFlowEatAndDrinkProps };

const makeEmptyBooking = (): BookingState => ({
  service: null,
  venue: null,
  occurrence: null,
  guestCount: null,
  date: null,
  room: null,
  time: null,
  isRequestAnyway: false,
  notes: '',
  groupNotes: {},
  selectedTags: [],
  selectedGroupTags: [],
  idempotencyKey: randomUuid(),
  seatingAreaPublicGuid: undefined,
});

// ── BookFlowEatAndDrink ───────────────────────────────────────────────────────

export function BookFlowEatAndDrink({ content, context }: BookFlowEatAndDrinkProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [reservation, setReservation] = useState<SeatingReservationDto | null>(null);
  const [errorToastMessage, setErrorToastMessage] = useState<string | null>(null);
  const [errorToastKey, setErrorToastKey] = useState(0);

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);
  const [booking, setBooking] = useState<BookingState>(makeEmptyBooking);

  const steps = content.properties?.steps?.items ?? [];

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else setStep(prev => (prev - 1) as 0 | 1 | 2);
  };

  const handleNext = () => setStep(prev => (prev + 1) as 0 | 1 | 2);

  const handleRequestSuccess = (res: SeatingReservationDto) => {
    setReservation(res);
    setStep(2);
  };

  const handleBookingError = () => {
    setErrorToastKey(key => key + 1);
    setErrorToastMessage(t('App.Labels.BookingError'));
    setStep(0);
  };

  return (
    <div className="pt-safe-area relative flex h-full flex-col bg-white">
      {steps.map((stepItem, index) => {
        if (index !== step) return null;
        const props = stepItem.content?.properties;
        const stepType = stepItem.content?.contentType;
        switch (stepType) {
          case EatAndDrinkBookFlowWhereHowManyWhenElementContentModelContentType.eatAndDrinkBookFlowWhereHowManyWhen:
            return (
              <EatAndDrinkBook
                key={index}
                cmsProps={props as EatAndDrinkBookFlowWhereHowManyWhenPropertiesModel}
                booking={booking}
                onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
                onBack={handleBack}
                onContinue={handleNext}
                onRequestSuccess={handleRequestSuccess}
                context={context}
                step={index + 2}
              />
            );
          case EatAndDrinkBookFlowMoreDetailsElementContentModelContentType.eatAndDrinkBookFlowMoreDetails:
            return (
              <EatAndDrinkDetails
                key={index}
                cmsProps={props as EatAndDrinkBookFlowMoreDetailsPropertiesModel}
                booking={booking}
                onBookingChange={(updates: Partial<BookingState>) =>
                  setBooking(prev => ({ ...prev, ...updates }))
                }
                onBack={handleBack}
                onSuccess={res => {
                  setReservation(res);
                  setStep(2);
                }}
                onError={handleBookingError}
                step={index + 2}
              />
            );
          case EatAndDrinkBookFlowConfirmationElementContentModelContentType.eatAndDrinkBookFlowConfirmation:
            if (!reservation) return null;
            return booking.isRequestAnyway ? (
              <ConfirmationRequest
                key={index}
                booking={booking}
                reservation={reservation}
                onGoHome={() => navigate('/')}
                onCancelRequest={() => navigate(-1)}
              />
            ) : (
              <ConfirmationBook
                key={index}
                booking={booking}
                reservation={reservation}
                cmsProps={props as EatAndDrinkBookFlowConfirmationPropertiesModel}
                onGoHome={() => navigate('/')}
                onSeeBookingDetails={() =>
                  navigate(
                    `${context?.settings?.properties?.bookingDetail?.route?.path}/${reservation.publicGuid}`
                  )
                }
                onBack={() => navigate(-1)}
              />
            );
          default:
            return null;
        }
      })}

      <Toast
        key={errorToastKey}
        variant="error"
        open={errorToastMessage !== null}
        message={errorToastMessage ?? ''}
        onDismiss={() => setErrorToastMessage(null)}
      />
    </div>
  );
}
